/**
 * Stacked speech-arrow geometry — authoritative sizing from BlockQuote’s
 * glass-border path. Shell scales; mid/fill step down by fixed ring widths so
 * the mid stroke matches the frosted rim thickness.
 */

export const SPEECH_BUBBLE_STACKED_ARROW_BASE_OUTER_HALF_WIDTH_PX = 10;
export const SPEECH_BUBBLE_STACKED_ARROW_BASE_OUTER_HEIGHT_PX = 13;
export const SPEECH_BUBBLE_STACKED_ARROW_BASE_SHELL_HALF_WIDTH_PX = 17; // 10 + 3 + 4
export const SPEECH_BUBBLE_STACKED_ARROW_BASE_SHELL_HEIGHT_PX = 21; // 13 + 3 + 5

/** Non-glass legacy: shell − outer half (17 − 10). */
export const SPEECH_BUBBLE_STACKED_ARROW_SHELL_TO_OUTER_HALF_LEGACY_PX = 7;
/** Non-glass legacy: shell − outer height (21 − 13). */
export const SPEECH_BUBBLE_STACKED_ARROW_SHELL_TO_OUTER_HEIGHT_LEGACY_PX = 8;
export const SPEECH_BUBBLE_STACKED_ARROW_OUTER_TO_INNER_HALF_PX = 1;
export const SPEECH_BUBBLE_STACKED_ARROW_OUTER_TO_INNER_HEIGHT_PX = 2;

export interface SpeechBubbleStackedArrowGeometry {
	shellHalfWidthPx: number;
	shellHeightPx: number;
	outerHalfWidthPx: number;
	outerHeightPx: number;
	innerHalfWidthPx: number;
	innerHeightPx: number;
	/** Toward-face nest into the glass / border rim. */
	stackUpshiftPx: number;
	/** Mid+fill shift toward face relative to shell (0 when glass). */
	borderInnerExtraDownPx: number;
}

export function resolveSpeechBubbleStackedArrowGeometry(params: {
	glassBorder: boolean;
	/** GlassBorder `borderWidth` (default 6). Ignored when `glassBorder` is false. */
	glassBorderWidthPx?: number;
	arrowScale?: number;
}): SpeechBubbleStackedArrowGeometry {
	const arrowScaleSafe =
		Number.isFinite(params.arrowScale) && (params.arrowScale ?? 0) > 0
			? (params.arrowScale as number)
			: 1;
	const glassBorderWidthPx = params.glassBorderWidthPx ?? 6;

	const shellToOuterHalfPx = params.glassBorder
		? glassBorderWidthPx + 4
		: SPEECH_BUBBLE_STACKED_ARROW_SHELL_TO_OUTER_HALF_LEGACY_PX;
	const shellToOuterHeightPx = params.glassBorder
		? glassBorderWidthPx + 4
		: SPEECH_BUBBLE_STACKED_ARROW_SHELL_TO_OUTER_HEIGHT_LEGACY_PX;

	const shellHalfWidthPx = Math.round(
		SPEECH_BUBBLE_STACKED_ARROW_BASE_SHELL_HALF_WIDTH_PX * arrowScaleSafe,
	);
	const shellHeightPx = Math.round(
		SPEECH_BUBBLE_STACKED_ARROW_BASE_SHELL_HEIGHT_PX * arrowScaleSafe,
	);

	const outerHalfWidthPx = params.glassBorder
		? Math.max(1, shellHalfWidthPx - shellToOuterHalfPx)
		: Math.round(
				SPEECH_BUBBLE_STACKED_ARROW_BASE_OUTER_HALF_WIDTH_PX *
					arrowScaleSafe,
			);
	const outerHeightPx = params.glassBorder
		? Math.max(1, shellHeightPx - shellToOuterHeightPx)
		: Math.round(
				SPEECH_BUBBLE_STACKED_ARROW_BASE_OUTER_HEIGHT_PX * arrowScaleSafe,
			);

	const innerHalfWidthPx = Math.max(
		1,
		outerHalfWidthPx - SPEECH_BUBBLE_STACKED_ARROW_OUTER_TO_INNER_HALF_PX,
	);
	const innerHeightPx = Math.max(
		1,
		outerHeightPx - SPEECH_BUBBLE_STACKED_ARROW_OUTER_TO_INNER_HEIGHT_PX,
	);

	return {
		shellHalfWidthPx,
		shellHeightPx,
		outerHalfWidthPx,
		outerHeightPx,
		innerHalfWidthPx,
		innerHeightPx,
		stackUpshiftPx: params.glassBorder ? glassBorderWidthPx + 2 : 3,
		borderInnerExtraDownPx: params.glassBorder ? 0 : 2,
	};
}
